import { ChangeDetectorRef, HostListener, TrackByFunction, Injectable } from '@angular/core';
import { ColumnOptions } from '@core';
import { TranscationDetailKeyKownService } from '@shared-feature/services/transcation-detail-key-kown.service';

// Interface to define the minimum required methods for dataSrv
export interface IPaginatedTableDataService {
  setComponentRef(component: any): void;
  parentComponent: any;
  newLineAdded$: any;
  itemDetails: any;
  addLines(count?: number): void;
  removeLine(formArray: any, index: number): void;
  dropLine(event: any): void;
}

@Injectable()
export abstract class PaginatedTableComponent<T extends IPaginatedTableDataService = IPaginatedTableDataService> {
  selectComponentArray: any;
  pageSize: number = 10;
  currentPage: number = 1;
  editableCells: any;
  newlyAddedLine: boolean = false;
  visibleColumns: ColumnOptions[] = [];
  
  protected dataSrv: T;
  protected cdr: ChangeDetectorRef;
  public keyDownService: TranscationDetailKeyKownService;

  constructor(
    dataSrv: T,
    cdr: ChangeDetectorRef,
    keyDownService: TranscationDetailKeyKownService,
  ) {
    this.dataSrv = dataSrv;
    this.cdr = cdr;
    this.keyDownService = keyDownService;
    this.dataSrv.setComponentRef(this);
    this.dataSrv.parentComponent = this;

    this.dataSrv.newLineAdded$.subscribe(() => {
      this.newlyAddedLine = true;
    });

   
  }

@HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent, globalRowIndex: number, columnName: string): void {
 
    // Get the elements with the class 'dropdownSelect' to check if a dropdown is open
    const isSelectOpen = Array.from(document.getElementsByClassName('ant-select-dropdown'))
      .filter((element) => 
        !element.classList.contains('ant-select-dropdown-hidden') &&
        element.getAttribute('ng-reflect-open') !== 'false'
      );
    
    // Update the column order based on the current page
    this.keyDownService.updateColumnOrder(this.visibleColumns);

    /**
     * This block handles the keydown events when the dropdown is not open.
     * If the dropdown is open or has options, the select component will handle its own keydown events.
     *
     * isSelectOpen.length = 0 indicates there is no dropdown.
     * isSelectOpen.length = 1 indicates there is a dropdown, but it does not have an option list to interact with.
     * In both cases, we want to handle the keydown event in this method.
     */
    if (
      ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(
        event.key
      ) &&
      isSelectOpen.length <= 1
    ) {
      let nextGlobalRowIndex = globalRowIndex;
      let nextColumnName = columnName;

      switch (event.key) {
        case 'ArrowDown':
          // Move to the next row
          if (isSelectOpen.length <= 1) nextGlobalRowIndex++;
          break;

        case 'ArrowUp':
          // Move to the previous row
          if (isSelectOpen.length <= 1) nextGlobalRowIndex--;
          break;

        case 'ArrowLeft':
          if (!this.selectComponentArray.includes(columnName)) {
            const element = document.activeElement as HTMLInputElement;

            if (element.selectionStart === 0) {
              // Move to the next column
              nextColumnName = this.keyDownService.getAdjacentColumn(
                columnName,
                -1
              );
            }
          } else {
            nextColumnName = this.keyDownService.getAdjacentColumn(
              columnName,
              -1
            );
          }
          break;
        case 'ArrowRight':
          if (!this.selectComponentArray.includes(columnName)) {
            const element = document.activeElement as HTMLInputElement;

            if (element.selectionEnd === element.value.length) {
              // Move to the next column
              nextColumnName = this.keyDownService.getAdjacentColumn(
                columnName,
                1
              );
            }
          } else {
            nextColumnName = this.keyDownService.getAdjacentColumn(
              columnName,
              1
            );
          }
          break;

        case 'Enter':
          // Handle the Enter key press
          if (event.ctrlKey) {
            // If Ctrl + Enter is pressed, add a new line and focus on the new line's select component
            this.addLines();
          } else if (!this.selectComponentArray.includes(columnName)) {
            /**
             * If Enter is pressed and the column is not a custom select component,
             * move to the next cell in the same row.
             * Custom select components handle their own navigation through `dataSrv.lineValueChanged`.
             */
            nextColumnName = this.keyDownService.getAdjacentColumn(
              columnName,
              1
            );
          }
          break;
      }

      // If the new row index is valid, move focus to the new cell
      if (
        this.keyDownService.isRowIndexValid(
          nextGlobalRowIndex,
          this.dataSrv.itemDetails.length
        )
      ) {
        // Calculate which page the target row is on
        const targetPage = Math.floor(nextGlobalRowIndex / this.pageSize) + 1;
        
        // If the target row is on a different page, navigate to that page first
        if (targetPage !== this.currentPage) {
          this.currentPage = targetPage;
          this.cdr.detectChanges();
          
          // After page change, focus on the correct cell using local page index
          setTimeout(() => {
            const localRowIndex = nextGlobalRowIndex % this.pageSize;
            this.keyDownService.focusNextCell(
              this.editableCells.toArray(),
              localRowIndex,
              nextColumnName
            );
          }, 100); // Small delay to ensure the new page is rendered
        } else {
          // If on the same page, use local row index
          const localRowIndex = nextGlobalRowIndex % this.pageSize;
          this.keyDownService.focusNextCell(
            this.editableCells.toArray(),
            localRowIndex,
            nextColumnName
          );
        }
      }
    }
  }


  addLines() {
    this.dataSrv.addLines();
    setTimeout(() => {
      this.scrollToLastRow();
    }, 100);
  }

  // Pagination methods
  get totalRowCount(): number {
    return this.dataSrv.itemDetails?.controls?.length || 0;
  }

  get totalPages(): number {
    return Math.ceil(this.totalRowCount / this.pageSize);
  }

  trackByRow: TrackByFunction<any> = (index: number, item: any): any => {
    return item;
  };

  getPaginatedData(): any[] {
    const controls = this.dataSrv.itemDetails?.controls || [];
    if (controls.length === 0) return [];

    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;

    return controls.slice(startIndex, endIndex);
  }

  getGlobalIndex(pageIndex: number): number {
    return (this.currentPage - 1) * this.pageSize + pageIndex;
  }

  getPageNumbers(): Array<number | string> {
    const totalPages = this.totalPages;
    const currentPage = this.currentPage;

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: Array<number | string> = [];

    if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      pages.push('...');
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  }

  getPaginationSummary(): string {
    const startItem = (this.currentPage - 1) * this.pageSize + 1;
    const endItem = Math.min(this.currentPage * this.pageSize, this.totalRowCount);
    return `${startItem} - ${endItem} of ${this.totalRowCount}`;
  }

  nextPage() {
    const totalPages = Math.ceil(this.totalRowCount / this.pageSize);
    if (this.currentPage < totalPages) {
      this.currentPage++;
      this.cdr.detectChanges();
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.cdr.detectChanges();
    }
  }

  goToPage(page: number) {
    const totalPages = Math.ceil(this.totalRowCount / this.pageSize);
    if (page >= 1 && page <= totalPages) {
      this.currentPage = page;
      this.cdr.detectChanges();
    }
  }

  onPageSizeChange(newPageSize: any): void {
    let actualPageSize: number;

    if (typeof newPageSize === 'object') {
      actualPageSize = newPageSize.value || newPageSize;
    } else if (typeof newPageSize === 'string') {
      actualPageSize = parseInt(newPageSize, 10);
    } else {
      actualPageSize = newPageSize;
    }

    this.pageSize = actualPageSize;
    this.currentPage = 1;
    this.cdr.detectChanges();
  }

  removeLineAtGlobalIndex(globalIndex: number) {
    this.dataSrv.removeLine(this.dataSrv.itemDetails, globalIndex);
    this.dataSrv.itemDetails.updateValueAndValidity();

    const totalRows = this.totalRowCount;
    const totalPages = Math.ceil(totalRows / this.pageSize);

    if (this.currentPage > 1 && this.currentPage > totalPages) {
      this.currentPage = totalPages;
    }

    if (this.currentPage < 1) {
      this.currentPage = 1;
    }

    this.cdr.detectChanges();
  }

  private scrollToLastRow() {
    this.currentPage = this.totalPages;
    this.cdr.detectChanges();
  }

  onDropLine(event: any) {
    // Convert local page indices to global indices
    const globalPreviousIndex = (this.currentPage - 1) * this.pageSize + event.previousIndex;
    const globalCurrentIndex = (this.currentPage - 1) * this.pageSize + event.currentIndex;
    
    // Create a modified event with global indices
    const modifiedEvent = {
      ...event,
      previousIndex: globalPreviousIndex,
      currentIndex: globalCurrentIndex,
      container: {
        ...event.container,
        data: this.dataSrv.itemDetails // Use the full array, not paginated data
      }
    };
    
    // Call the original dropLine method with the modified event
    this.dataSrv.dropLine(modifiedEvent);
  }
}
